import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import authFetch from '../utils/authFetch';
import { 
    ChartBarIcon, 
    ArrowTrendingUpIcon, 
    ArrowTrendingDownIcon,
    StarIcon,
    DocumentTextIcon,
    TrophyIcon
} from '@heroicons/react/24/outline';

const AnalysisStats = () => {
    const [analyses, setAnalyses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState('month');
    const [error, setError] = useState('');

    const token = useSelector((state) => state.auth.token);
    const userId = useSelector((state) => state.auth.user.id);

    useEffect(() => {
        fetchAnalysisHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAnalysisHistory = async () => {
        try {
            const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/history/${userId}`);

            if (!response.ok) {
                throw new Error('Erreur lors de la récupération des données');
            }

            const data = await response.json();
            setAnalyses(data.analyses || []);
        } catch (error) {
            setError('Impossible de charger les statistiques');
            console.error('Erreur:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getPeriodData = () => {
        const now = new Date();
        let startDate;

        switch (selectedPeriod) {
            case 'week':
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case 'month':
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
            case 'year':
                startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
                break;
            default:
                startDate = new Date(0);
        }

        return analyses.filter(analysis => new Date(analysis.createdAt) >= startDate);
    };

    const calculateStats = () => {
        const periodData = getPeriodData();
        
        if (periodData.length === 0) {
            return {
                totalAnalyses: 0,
                averageScore: 0,
                bestScore: 0,
                worstScore: 0,
                sectorBreakdown: {},
                scoreDistribution: { excellent: 0, good: 0, average: 0, poor: 0 },
                trend: 'stable',
                topSectors: [],
                recentImprovement: 0
            };
        }

        const scores = periodData.map(a => a.score || 0).filter(s => s > 0);
        const averageScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
        const bestScore = Math.max(...scores, 0);
        const worstScore = Math.min(...scores, 0);

        // Répartition par secteur
        const sectorBreakdown = {};
        periodData.forEach(analysis => {
            const sector = analysis.sector || 'Non spécifié';
            sectorBreakdown[sector] = (sectorBreakdown[sector] || 0) + 1;
        });

        // Distribution des scores
        const scoreDistribution = {
            excellent: scores.filter(s => s >= 80).length,
            good: scores.filter(s => s >= 60 && s < 80).length,
            average: scores.filter(s => s >= 40 && s < 60).length,
            poor: scores.filter(s => s < 40).length
        };

        // Top secteurs
        const topSectors = Object.entries(sectorBreakdown)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 5)
            .map(([sector, count]) => ({ sector, count }));

        // Tendance
        const recentScores = periodData
            .slice(-5)
            .map(a => a.score || 0)
            .filter(s => s > 0);
        
        let trend = 'stable';
        if (recentScores.length >= 2) {
            const firstHalf = recentScores.slice(0, Math.floor(recentScores.length / 2));
            const secondHalf = recentScores.slice(Math.floor(recentScores.length / 2));
            const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
            const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
            
            if (secondAvg > firstAvg + 5) trend = 'up';
            else if (secondAvg < firstAvg - 5) trend = 'down';
        }

        // Amélioration récente
        const recentImprovement = recentScores.length >= 2 
            ? Math.round(((recentScores[recentScores.length - 1] - recentScores[0]) / recentScores[0]) * 100)
            : 0;

        return {
            totalAnalyses: periodData.length,
            averageScore,
            bestScore,
            worstScore,
            sectorBreakdown,
            scoreDistribution,
            trend,
            topSectors,
            recentImprovement
        };
    };

    const stats = calculateStats();

    if (isLoading) {
        return (
            <div className="page-shell flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement des statistiques...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell p-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2">Statistiques d'analyse</h1>
                    <p className="text-gray-600">Analysez vos performances et progressez dans votre carrière</p>
                </div>

                {/* Period Selector */}
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-800">Période d'analyse</h2>
                        <select
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="week">Cette semaine</option>
                            <option value="month">Ce mois</option>
                            <option value="year">Cette année</option>
                            <option value="all">Toute la période</option>
                        </select>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-red-600">{error}</p>
                    </div>
                )}

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Analyses totales</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.totalAnalyses}</p>
                            </div>
                            <DocumentTextIcon className="w-8 h-8 text-blue-500" />
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Score moyen</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.averageScore}/100</p>
                            </div>
                            <StarIcon className="w-8 h-8 text-yellow-500" />
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Meilleur score</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.bestScore}/100</p>
                            </div>
                            <TrophyIcon className="w-8 h-8 text-green-500" />
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Tendance</p>
                                <div className="flex items-center">
                                                                {stats.trend === 'up' ? (
                                <ArrowTrendingUpIcon className="w-6 h-6 text-green-500 mr-2" />
                            ) : stats.trend === 'down' ? (
                                <ArrowTrendingDownIcon className="w-6 h-6 text-red-500 mr-2" />
                            ) : (
                                <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                            )}
                                    <span className="text-lg font-semibold capitalize">{stats.trend}</span>
                                </div>
                            </div>
                            <ChartBarIcon className="w-8 h-8 text-purple-500" />
                        </div>
                    </div>
                </div>

                {/* Detailed Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Score Distribution */}
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-6">Distribution des scores</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-600">Excellent (80-100)</span>
                                <div className="flex items-center">
                                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                                        <div 
                                            className="bg-green-500 h-2 rounded-full transition-all duration-1000"
                                            style={{ width: `${stats.totalAnalyses > 0 ? (stats.scoreDistribution.excellent / stats.totalAnalyses) * 100 : 0}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900">{stats.scoreDistribution.excellent}</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-600">Bon (60-79)</span>
                                <div className="flex items-center">
                                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                                        <div 
                                            className="bg-yellow-500 h-2 rounded-full transition-all duration-1000"
                                            style={{ width: `${stats.totalAnalyses > 0 ? (stats.scoreDistribution.good / stats.totalAnalyses) * 100 : 0}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900">{stats.scoreDistribution.good}</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-600">Moyen (40-59)</span>
                                <div className="flex items-center">
                                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                                        <div 
                                            className="bg-orange-500 h-2 rounded-full transition-all duration-1000"
                                            style={{ width: `${stats.totalAnalyses > 0 ? (stats.scoreDistribution.average / stats.totalAnalyses) * 100 : 0}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900">{stats.scoreDistribution.average}</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-600">À améliorer (0-39)</span>
                                <div className="flex items-center">
                                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                                        <div 
                                            className="bg-red-500 h-2 rounded-full transition-all duration-1000"
                                            style={{ width: `${stats.totalAnalyses > 0 ? (stats.scoreDistribution.poor / stats.totalAnalyses) * 100 : 0}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900">{stats.scoreDistribution.poor}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Sectors */}
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-6">Secteurs les plus analysés</h3>
                        <div className="space-y-4">
                            {stats.topSectors.length > 0 ? (
                                stats.topSectors.map((sector, index) => (
                                    <div key={sector.sector} className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <span className="text-sm font-medium text-gray-600 w-8">{index + 1}.</span>
                                            <span className="text-sm font-medium text-gray-800">{sector.sector}</span>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                                                <div 
                                                    className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
                                                    style={{ width: `${(sector.count / stats.totalAnalyses) * 100}%` }}
                                                ></div>
                                            </div>
                                            <span className="text-sm font-semibold text-gray-900">{sector.count}</span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Insights */}
                <div className="bg-white rounded-2xl shadow-xl p-6 mt-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-6">Insights et recommandations</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Progression récente</h4>
                            <p className="text-sm text-gray-600">
                                {stats.recentImprovement > 0 
                                    ? `Votre score s'est amélioré de ${Math.abs(stats.recentImprovement)}% récemment. Continuez sur cette lancée !`
                                    : stats.recentImprovement < 0
                                    ? `Votre score a diminué de ${Math.abs(stats.recentImprovement)}%. Concentrez-vous sur les suggestions d'amélioration.`
                                    : 'Votre performance est stable. Essayez de nouveaux secteurs pour progresser.'
                                }
                            </p>
                        </div>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Conseils personnalisés</h4>
                            <p className="text-sm text-gray-600">
                                {stats.averageScore >= 80 
                                    ? 'Excellent travail ! Votre CV est très compétitif. Concentrez-vous sur la personnalisation par secteur.'
                                    : stats.averageScore >= 60
                                    ? 'Bon niveau ! Améliorez les sections faibles pour atteindre l\'excellence.'
                                    : 'Il y a du potentiel ! Suivez attentivement les suggestions pour améliorer significativement votre CV.'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalysisStats; 